"use client";

import { useCallback, useEffect, useState } from "react";
import { SoftphoneDrawer } from "@/components/telephony/SoftphoneDrawer";
import { SOFTPHONE_DIAL_EVENT, SOFTPHONE_OPEN_EVENT, SOFTPHONE_WORKFLOW_EVENT } from "@/components/telephony/events";
import { useCustomerCallAction } from "@/components/telephony/useCustomerCallAction";

type TelephonyHostProps = {
  mode?: "overlay" | "dock";
  onOpenChange?: (open: boolean) => void;
};

export function TelephonyHost({
  mode = "overlay",
  onOpenChange,
}: TelephonyHostProps) {
  const [open, setOpen] = useState(false);
  const { callModal, openManualCallDialer } = useCustomerCallAction();

  const setPhoneOpen = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }, [onOpenChange]);

  useEffect(() => {
    const openSoftphone = () => {
      const handled = openManualCallDialer();
      if (!handled) setPhoneOpen(true);
    };
    const openWorkflowSoftphone = () => setPhoneOpen(true);
    const openFromFrame = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === SOFTPHONE_OPEN_EVENT) openSoftphone();
      if (event.data?.type === SOFTPHONE_DIAL_EVENT) {
        setPhoneOpen(true);
        window.dispatchEvent(new CustomEvent(SOFTPHONE_DIAL_EVENT, { detail: event.data.detail || {} }));
      }
    };
    window.addEventListener(SOFTPHONE_OPEN_EVENT, openSoftphone);
    window.addEventListener(SOFTPHONE_WORKFLOW_EVENT, openWorkflowSoftphone);
    window.addEventListener("message", openFromFrame);
    return () => {
      window.removeEventListener(SOFTPHONE_OPEN_EVENT, openSoftphone);
      window.removeEventListener(SOFTPHONE_WORKFLOW_EVENT, openWorkflowSoftphone);
      window.removeEventListener("message", openFromFrame);
    };
  }, [openManualCallDialer, setPhoneOpen]);

  return (
    <>
      <SoftphoneDrawer
        open={open}
        variant={mode === "dock" ? "dock" : "drawer"}
        onRequestOpen={() => setPhoneOpen(true)}
        onClose={() => setPhoneOpen(false)}
      />
      {callModal}
    </>
  );
}

export { SOFTPHONE_OPEN_EVENT };
